import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import * as api from "../../services/api";
import "./AdminDashboard.css";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalCategories: 0
  });
  const [loading, setLoading] = useState(true);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    // Get pinned state from localStorage, default to true
    const saved = localStorage.getItem('adminSidebarPinned');
    return saved !== null ? saved === 'true' : true;
  });
  const [sidebarOpen, setSidebarOpen] = useState(sidebarPinned); // Default to pinned state
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const lastOrderCountRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated || !user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    loadStats();
    
    // Set up polling to check for new orders every 30 seconds
    const pollInterval = setInterval(() => {
      checkForNewOrders();
    }, 30000); // Check every 30 seconds

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => clearInterval(pollInterval);
  }, [user, isAuthenticated, navigate]);

  // Initialize last order count after first load
  useEffect(() => {
    if (stats.totalOrders > 0 && lastOrderCountRef.current === 0) {
      setLastOrderCount(stats.totalOrders);
      lastOrderCountRef.current = stats.totalOrders;
    }
  }, [stats.totalOrders]);

  const checkForNewOrders = async () => {
    try {
      const ordersData = await api.getAllOrders();
      const currentOrderCount = ordersData.length;
      const prevCount = lastOrderCountRef.current;
      
      if (currentOrderCount > prevCount && prevCount > 0) {
        const newCount = currentOrderCount - prevCount;
        setNewOrdersCount(prev => prev + newCount);
        setLastOrderCount(currentOrderCount);
        lastOrderCountRef.current = currentOrderCount;
        
        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('New Order Received! 🎉', {
            body: `${newCount} new order${newCount > 1 ? 's' : ''} has been placed.`,
            icon: '/favicon.ico',
            tag: 'new-order',
            requireInteraction: false
          });
        }
      } else if (prevCount === 0 && currentOrderCount > 0) {
        // First time loading - just set the count
        setLastOrderCount(currentOrderCount);
        lastOrderCountRef.current = currentOrderCount;
      } else if (currentOrderCount !== prevCount) {
        // Update if count changed (e.g., order deleted)
        setLastOrderCount(currentOrderCount);
        lastOrderCountRef.current = currentOrderCount;
      }
    } catch (error) {
      console.error('Error checking for new orders:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Load basic stats (you can create admin stats endpoint later)
      const [products, orders, categories] = await Promise.all([
        api.getProducts().catch(() => []),
        api.getAllOrders().catch(() => []), // Use getAllOrders for admin
        api.getCategories().catch(() => [])
      ]);

      const ordersCount = orders.length || 0;
      
      setStats({
        totalProducts: products.length || 0,
        totalOrders: ordersCount,
        totalUsers: 0, // Will need admin endpoint
        totalCategories: categories.length || 0
      });

      // Initialize last order count if not set
      if (ordersCount > 0 && lastOrderCount === 0) {
        setLastOrderCount(ordersCount);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const togglePinSidebar = () => {
    const newPinnedState = !sidebarPinned;
    setSidebarPinned(newPinnedState);
    localStorage.setItem('adminSidebarPinned', newPinnedState.toString());
    // If pinning, also open the sidebar
    if (newPinnedState) {
      setSidebarOpen(true);
    }
  };

  // Sync sidebar state with pinned state
  useEffect(() => {
    if (sidebarPinned) {
      setSidebarOpen(true);
    }
  }, [sidebarPinned]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`admin-dashboard ${sidebarOpen && sidebarPinned ? 'sidebar-pinned-open' : ''}`}>
      <button 
        className="hamburger-menu-toggle"
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>
      <div 
        className={`sidebar-overlay ${sidebarOpen && !sidebarPinned ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${sidebarPinned ? 'pinned' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="sidebar-header-content">
            <h2>AVRNHH Admin</h2>
            <p>{user?.email}</p>
          </div>
          <button
            className="sidebar-pin-btn"
            onClick={togglePinSidebar}
            aria-label={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}
            title={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          >
            {sidebarPinned ? '📌' : '📍'}
          </button>
        </div>

        <nav className="admin-nav">
          <button
            className={`admin-nav-item ${location.pathname === '/admin' || location.pathname === '/admin/dashboard' ? 'active' : ''}`}
            onClick={() => {
              navigate('/admin/dashboard');
              setSidebarOpen(false);
            }}
          >
            📊 Dashboard
          </button>
          <button
            className={`admin-nav-item ${location.pathname.includes('/admin/products') ? 'active' : ''}`}
            onClick={() => {
              navigate('/admin/products');
              setSidebarOpen(false);
            }}
          >
            📦 Products
          </button>
          <button
            className={`admin-nav-item ${location.pathname.includes('/admin/orders') ? 'active' : ''}`}
            onClick={() => {
              setNewOrdersCount(0); // Clear notification when navigating to orders
              navigate('/admin/orders');
              setSidebarOpen(false);
            }}
            style={{ position: 'relative' }}
          >
            🛒 Orders
            {newOrdersCount > 0 && (
              <span className="order-notification-badge">{newOrdersCount}</span>
            )}
          </button>
          <button
            className={`admin-nav-item ${location.pathname.includes('/admin/categories') ? 'active' : ''}`}
            onClick={() => {
              navigate('/admin/categories');
              setSidebarOpen(false);
            }}
          >
            📁 Categories
          </button>
          <button
            className={`admin-nav-item ${location.pathname.includes('/admin/units') ? 'active' : ''}`}
            onClick={() => {
              navigate('/admin/units');
              setSidebarOpen(false);
            }}
          >
            📏 Units
          </button>
          <button
            className={`admin-nav-item ${location.pathname.includes('/admin/coupons') ? 'active' : ''}`}
            onClick={() => {
              navigate('/admin/coupons');
              setSidebarOpen(false);
            }}
          >
            🎟️ Coupons
          </button>
          <button
            className={`admin-nav-item ${location.pathname.includes('/admin/users') ? 'active' : ''}`}
            onClick={() => {
              navigate('/admin/users');
              setSidebarOpen(false);
            }}
          >
            👥 Users
          </button>
        </nav>

        <div className="admin-sidebar-footer">
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </aside>

      <main className={`admin-main ${sidebarOpen && sidebarPinned ? 'sidebar-open' : 'sidebar-closed'}`}>
        <Outlet />
      </main>
    </div>
  );
}

